import { useEffect } from 'react';

import { useCms } from '~/components/hooks/useCms';
import useWindowFocus from '~/components/hooks/useWindowFocus';
import useDataFetcher from '~/components/useDataFetcher';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import { sorActiveAttemptUrl } from './api';
import { SOR_CLAIM_LOCK_CHANGED_EVENT } from './sorClaimLockSignal';
import TEXTS from './texts';
import type { SorActiveAttemptResponse, SorClaimLockAttempt } from './types';

const REFETCH_INTERVAL_MS = 30_000;

export interface SorClaimLock {
  /** The claim's open attempt, or null when the SoR has nothing left to confirm on it. */
  attempt: SorClaimLockAttempt | null;
  sorName: string;
  /** Whether the organization has the System of Record flag: without it, nothing on the claim waits on an SoR. */
  isSorEnabled: boolean;
  reload: () => void;
}

/**
 * The claim's lock while its organization's System of Record has yet to confirm a change on it. Read on mount,
 * every 30 seconds, when the window regains focus, and whenever a request comes back with an SoR outcome; not
 * read at all for an organization without the System of Record flag.
 */
export const useSorClaimLock = (claimId: number): SorClaimLock => {
  const { userOrganization } = useCms();
  const isSorEnabled = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.SYSTEM_OF_RECORD);

  const { data, reloadData } = useDataFetcher<SorActiveAttemptResponse>(
    sorActiveAttemptUrl(userOrganization.id, claimId),
    undefined,
    { shouldFetch: isSorEnabled, shouldOpenErrorDialog: false, refetchInterval: REFETCH_INTERVAL_MS }
  );

  useWindowFocus({ onFocus: reloadData, disabled: !isSorEnabled });

  useEffect(() => {
    if (!isSorEnabled) {
      return undefined;
    }
    window.addEventListener(SOR_CLAIM_LOCK_CHANGED_EVENT, reloadData);
    return () => window.removeEventListener(SOR_CLAIM_LOCK_CHANGED_EVENT, reloadData);
  }, [isSorEnabled, reloadData]);

  return {
    attempt: data?.attempt ?? null,
    sorName: data?.sor.name ?? TEXTS.defaultSorName,
    isSorEnabled,
    reload: reloadData,
  };
};
